import {
  useGetAlphaGeminiDiscoverLatestQuery,
  useAlphaGeminiDiscoverRunNowMutation,
} from "@/store/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

/**
 * GeminiDiscoverPanel — surfaces the most recent Gemini-driven BSC alpha
 * discovery run. Shows what Gemini Pro+Search proposed, what was added to
 * the watchlist, and what was rejected (and why). Daily cron at 24h
 * cadence; user can also force a run-now.
 */
export function GeminiDiscoverPanel() {
  const latest = useGetAlphaGeminiDiscoverLatestQuery();
  const [runNow, { isLoading: running }] = useAlphaGeminiDiscoverRunNowMutation();

  async function onRunNow() {
    try {
      const r: any = await runNow().unwrap();
      if (r?.status === "ok") {
        toast.success(
          (r.added?.length || 0) > 0
            ? `Gemini added ${r.added.length} new tokens: ${r.added.map((a: any) => a.symbol).join(", ")}`
            : `Gemini parsed ${r.candidates_parsed} candidates, 0 new added (${r.skipped?.length || 0} skipped)`
        );
      } else if (r?.status === "disabled") {
        toast.info("Gemini discovery is disabled in settings");
      } else {
        toast.error(`Gemini discovery failed: ${r?.error ?? r?.reason ?? "?"}`);
      }
    } catch (e: any) {
      toast.error(`run failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  const d: any = latest.data;
  const hasRun = d && d?.status !== "no_runs_yet" && d?.ts;
  const added = (d?.added as any[]) || [];
  const skipped = (d?.skipped as any[]) || [];
  const candidates = (d?.candidates as any[]) || [];
  const tsStr = hasRun ? new Date(d.ts).toLocaleString() : null;

  return (
    <div className="mb-3 p-3 rounded border border-border bg-bg2/40 text-sm">
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold">🤖 Gemini Pro Discovery</span>
          <Badge tone="info">Auto · 24h</Badge>
          {hasRun ? (
            <span className="text-xs text-muted">Last run {tsStr}</span>
          ) : (
            <span className="text-xs text-muted italic">Not run yet</span>
          )}
          {hasRun && d?.error && (
            <Badge tone="bad" title={d.error}>error</Badge>
          )}
        </div>
        <Button size="sm" variant="primary" onClick={onRunNow} disabled={running}>
          {running ? "Asking Gemini…" : "🤖 Run discovery now"}
        </Button>
      </div>

      {!hasRun && (
        <p className="text-xs text-muted italic">
          Click "Run discovery now" to ask Gemini Pro + Google Search for
          currently-trending BSC tokens. Results are added to the watchlist
          below with Gemini's rationale stored in the token's notes.
        </p>
      )}

      {hasRun && (
        <div className="grid gap-3 md:grid-cols-2">
          {/* ADDED */}
          <div>
            <div className="text-xs font-medium text-muted mb-1">
              Added this run · {added.length}
            </div>
            {added.length === 0 ? (
              <div className="text-xs italic text-muted">(none)</div>
            ) : (
              <ul className="space-y-1">
                {added.map((a) => (
                  <li key={a.contract} className="text-xs">
                    <a
                      href={`https://dexscreener.com/bsc/${a.contract}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono font-semibold underline hover:text-accent"
                    >
                      {a.symbol}
                    </a>{" "}
                    <span className="text-muted">
                      ${Number(a.price ?? 0).toExponential(2)} ·
                      vol${Number(a.volume_24h ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} ·
                      liq${Number(a.liquidity ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    {a.blurb && (
                      <div className="text-muted ml-2 italic" title={a.blurb}>
                        {a.blurb.length > 160 ? a.blurb.slice(0, 160) + "…" : a.blurb}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* SKIPPED */}
          <div>
            <div className="text-xs font-medium text-muted mb-1">
              Skipped · {skipped.length}
              {candidates.length > 0 && (
                <span className="text-muted/70 ml-2">
                  (of {candidates.length} parsed)
                </span>
              )}
            </div>
            {skipped.length === 0 ? (
              <div className="text-xs italic text-muted">(none)</div>
            ) : (
              <ul className="space-y-1 max-h-48 overflow-auto">
                {skipped.map((s, i) => (
                  <li key={`${s.contract}-${i}`} className="text-xs">
                    <span className="font-mono">{s.symbol}</span>{" "}
                    <span className="text-muted">— {s.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
